import { CheckboxField } from '@hologram-dimension/checkbox-field';
import { TextInput } from '@hologram-dimension/text-input';
import { Label } from '@hologram-dimension/label';
import {
  RefinementListProps,
  UseRefinementListProps,
  useRefinementList,
} from 'react-instantsearch';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Icon } from '@hologram-dimension/icon';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import { ratDisplayOrder, transformRAT } from './searchUtils';
import { RAT } from './searchTypes';
import styles from './RefinementList.module.css';

interface HighlightQueryProps {
  input: string;
  query: string;
}

const HighlightQuery = (props: HighlightQueryProps) => {
  const { input, query } = props;
  if (!query) {
    return input; // Return the input string unchanged if the query is empty
  }

  // Escape special characters in the query for use in a regular expression
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create a regular expression to find all occurrences of the query (case-insensitive)
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  // Split the input string into an array of matched and unmatched parts
  const parts = input.split(regex);

  // Map over the parts and wrap matched parts in <span>
  return (
    <>
      {parts.map((part, index) =>
        regex.test(part) ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} className={styles.highlightedText}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

const RefinementList = (props: UseRefinementListProps) => {
  const { attribute } = props;
  const sortBy: RefinementListProps['sortBy'] | undefined = useMemo(
    () =>
      attribute === 'country'
        ? (a, b) => {
            // Define pinned values and their order
            const pinnedValues = ['United States', 'Canada'];

            // First priority: isRefined (refined items first)
            if (a.isRefined !== b.isRefined) {
              return a.isRefined ? -1 : 1;
            }

            // Second priority: pinned values
            const aIsPinned = pinnedValues.includes(a.name);
            const bIsPinned = pinnedValues.includes(b.name);

            if (aIsPinned && bIsPinned) {
              // Both are pinned, sort by their pinned order
              return pinnedValues.indexOf(a.name) - pinnedValues.indexOf(b.name);
            }
            if (aIsPinned) {
              return -1; // a is pinned, it comes first
            }
            if (bIsPinned) {
              return 1; // b is pinned, it comes first
            }

            // Third priority: alphabetical by name
            return a.name.localeCompare(b.name);
          }
        : undefined,
    [attribute]
  );
  const { items, refine, searchForItems } = useRefinementList({ ...props, sortBy });
  const [countryQuery, setCountryQuery] = useState<string>('');
  const { sendAnalyticsEvent } = useAnalytics();

  switch (attribute) {
    case 'supported_rat': {
      const orderedRats = items.sort(
        (a, b) => ratDisplayOrder.indexOf(a.value as RAT) - ratDisplayOrder.indexOf(b.value as RAT)
      );
      return (
        <CheckboxField
          label="RATs"
          name="rat-filter"
          options={orderedRats.map((item) => ({
            label: `${transformRAT(item.label as RAT)} (${item.count})`,
            labelNode: (
              <span>
                {transformRAT(item.label as RAT, 'long')}&nbsp;&nbsp;
                <span className={styles.diminishedText}>({item.count})</span>
              </span>
            ),
            value: item.value,
            defaultChecked: item.isRefined,
          }))}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
            refine(target.value);
            if (sendAnalyticsEvent && target.checked) {
              sendAnalyticsEvent('Network Coverage - Filter - RAT', {
                rat: (target as HTMLInputElement).value,
              });
            }
          }}
        />
      );
    }
    case 'country':
      return (
        <div className={styles.countryListControl}>
          <Label>Country</Label>
          <TextInput
            id="country-search"
            type="search"
            autocomplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Filter countries"
            iconStart={<Icon name="Search" size="medium" />}
            inputProps={{
              onChange: ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
                searchForItems(currentTarget.value);
                setCountryQuery(currentTarget.value);
              },
            }}
          />
          {items.length > 0 ? (
            <CheckboxField
              label="Country"
              hiddenLabel
              name="country-filter"
              options={items.map((item) => ({
                label: `${item.label} (${item.count})`,
                labelNode: (
                  <span>
                    <HighlightQuery input={item.label} query={countryQuery} />
                    &nbsp;&nbsp;
                    <span className={styles.diminishedText}>({item.count})</span>
                  </span>
                ),
                value: item.value,
                defaultChecked: item.isRefined,
                id: item.value,
              }))}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
                refine(target.value);
                if (sendAnalyticsEvent && target.checked) {
                  sendAnalyticsEvent('Network Coverage - Filter - Country', {
                    country: target.value,
                  });
                }
              }}
            />
          ) : (
            <span className={clsx(styles.noMatches, styles.diminishedText)}>No matches</span>
          )}
        </div>
      );

    default:
      return null;
  }
};

export default RefinementList;
